import { CONTACT_SALES_URL, CREDIT_DETAILS_PATH } from "./constants";

type PaymentRequiredPayload = {
  message?: unknown;
  code?: unknown;
  details?: unknown;
};

type PaymentRequiredDetails = {
  reason?: unknown;
  credits_limit?: unknown;
  credits_used?: unknown;
  credits_remaining?: unknown;
  average_analysis_credits?: unknown;
  projected_credits_used?: unknown;
  projected_credits_over_limit?: unknown;
  can_start_analysis?: unknown;
  details_path?: unknown;
  contact_url?: unknown;
};

type CreditLimitBlockReason =
  "PROJECTED_ANALYSIS_CREDIT_LIMIT_EXCEEDED" | "NO_ACTIVE_SUBSCRIPTION";

export type InsufficientCreditsError = {
  reason: CreditLimitBlockReason;
  message: string;
  creditsLimit?: number;
  creditsUsed?: number;
  creditsRemaining?: number;
  averageAnalysisCredits?: number;
  projectedCreditsUsed?: number;
  projectedCreditsOverLimit?: number;
  canStartAnalysis?: boolean;
  detailsPath: string;
  contactUrl?: string;
};

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isCreditLimitBlockReason(
  value: unknown,
): value is CreditLimitBlockReason {
  return (
    value === "PROJECTED_ANALYSIS_CREDIT_LIMIT_EXCEEDED" ||
    value === "NO_ACTIVE_SUBSCRIPTION"
  );
}

function roundCredits(value: number): number {
  return Math.round(value * 100) / 100;
}

export function parseInsufficientCreditsError(
  error: unknown,
): InsufficientCreditsError | null {
  if (!error || typeof error !== "object") return null;

  const payload = error as PaymentRequiredPayload;
  if (payload.code !== "PAYMENT_REQUIRED") return null;
  if (!payload.details || typeof payload.details !== "object") return null;

  const details = payload.details as PaymentRequiredDetails;
  const reason = isCreditLimitBlockReason(details.reason)
    ? details.reason
    : "PROJECTED_ANALYSIS_CREDIT_LIMIT_EXCEEDED";

  const message =
    typeof payload.message === "string"
      ? payload.message
      : reason === "NO_ACTIVE_SUBSCRIPTION"
        ? "No active subscription was found for this organization."
        : "Credit limit reached. Please review your credits and contact sales to continue.";

  if (reason === "NO_ACTIVE_SUBSCRIPTION") {
    return {
      reason,
      message,
      detailsPath:
        typeof details.details_path === "string"
          ? details.details_path
          : CREDIT_DETAILS_PATH,
      contactUrl:
        typeof details.contact_url === "string"
          ? details.contact_url
          : CONTACT_SALES_URL,
    };
  }

  if (
    !isNumber(details.credits_limit) ||
    !isNumber(details.credits_used) ||
    !isNumber(details.average_analysis_credits) ||
    !isNumber(details.projected_credits_used)
  ) {
    return null;
  }

  const projectedCreditsOverLimit = isNumber(
    details.projected_credits_over_limit,
  )
    ? details.projected_credits_over_limit
    : Math.max(
        roundCredits(details.projected_credits_used - details.credits_limit),
        0,
      );

  return {
    reason,
    message,
    creditsLimit: details.credits_limit,
    creditsUsed: details.credits_used,
    creditsRemaining: isNumber(details.credits_remaining)
      ? details.credits_remaining
      : roundCredits(details.credits_limit - details.credits_used),
    averageAnalysisCredits: details.average_analysis_credits,
    projectedCreditsUsed: details.projected_credits_used,
    projectedCreditsOverLimit,
    canStartAnalysis:
      typeof details.can_start_analysis === "boolean"
        ? details.can_start_analysis
        : details.projected_credits_used <= details.credits_limit,
    detailsPath:
      typeof details.details_path === "string"
        ? details.details_path
        : CREDIT_DETAILS_PATH,
    contactUrl:
      typeof details.contact_url === "string"
        ? details.contact_url
        : CONTACT_SALES_URL,
  };
}
